import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperience, IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { flatten } from 'lodash';
import type { ExperienceProblemData } from '../../InstanceProblems';
import { buildIssue, Issue, IssueCopyContext } from '../types';
import { collectAssigneeBounceIssues } from './assigneeBounce';
import { collectAutomationIssues } from './automation';
import { collectCCIssues } from './cc';
import { collectInstanceIssues } from './instanceSubtask';
import { collectMissingSupportingActors } from './missingSupportingActors';
import { withoutSyncFailuresCoveredByWriteErrors } from './coveredSyncFailures';
import { collectMissingVariables } from './missingVariables';
import { collectSentItemIssues } from './sentItem';

// Mirrors `DEACTIVATE_MISSING_VARIABLE_ISSUES` in ../../InstanceProblems.tsx —
// this collector is effectively dead in production today; kept in sync so a
// future re-enable only has to flip one flag, not two.
const DEACTIVATE_MISSING_VARIABLE_ISSUES = true;

/** New-copy/new-UI grouping pipeline's collection entry point — normalizes
 * every raw error family into `Issue[]` via the shared taxonomy's
 * `buildIssue`. Mirrors `collectInstanceErrors` in ../../InstanceProblems.tsx
 * (the legacy, ungrouped pipeline) but outputs `Issue[]`. */
export const collectAllIssues = async (
  tpl: Doc<IExperienceTemplate>,
  ds: Doc<IExperience>[],
  org: IOrganization,
  ctx: IssueCopyContext
): Promise<Issue[]> => {
  const fullCtx: IssueCopyContext = {
    ...ctx,
    supportingActors: ctx.supportingActors || tpl.data.supportingActors
  };

  const missingVarIssues = DEACTIVATE_MISSING_VARIABLE_ISSUES
    ? {}
    : await collectMissingVariables(tpl, ds, org);

  const rawIssues = ds.reduce<ExperienceProblemData[]>((m, d) => {
    const instanceIssues = collectInstanceIssues(d.data.instanceErrors, tpl, d);
    return [
      ...m,
      ...withoutSyncFailuresCoveredByWriteErrors(collectAutomationIssues(tpl, d), instanceIssues),
      ...collectSentItemIssues(tpl, d),
      ...collectMissingSupportingActors(tpl, d),
      ...instanceIssues,
      ...collectCCIssues(tpl, d),
      ...collectAssigneeBounceIssues(tpl, d)
    ];
  }, []);

  const allRaw = [...rawIssues, ...flatten(Object.values(missingVarIssues))];
  return allRaw.map((d) => buildIssue(d, fullCtx));
};
